import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { Loader2, Download, Share2, ArrowRight, Copy, Check, ImageDown } from "lucide-react";
import { api, track } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";
import { CircleBoard } from "@/components/CircleBoard";
import { toast } from "sonner";

const THEMES = [
  { key: "indigo", nameKey: "themeIndigo", bg: "from-slate-950 via-indigo-900 to-indigo-600", accent: "text-amber-300", swatch: "from-slate-950 via-indigo-800 to-indigo-500" },
  { key: "coral", nameKey: "themeCoral", bg: "from-rose-700 via-orange-500 to-amber-400", accent: "text-yellow-100", swatch: "from-rose-600 via-orange-500 to-amber-300" },
  { key: "mint", nameKey: "themeMint", bg: "from-emerald-900 via-teal-600 to-emerald-400", accent: "text-lime-200", swatch: "from-emerald-800 via-teal-500 to-emerald-300" },
];

function ThemePicker({ theme, setTheme, t }) {
  return (
    <div className="mt-5" data-testid="theme-picker">
      <div className="mb-2 text-center text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">{t("theme")}</div>
      <div className="flex justify-center gap-3">
        {THEMES.map((th) => (
          <button key={th.key} onClick={() => setTheme(th.key)} data-testid={`theme-${th.key}-btn`} aria-pressed={theme === th.key}
            className={`flex flex-col items-center gap-1.5 rounded-2xl px-2 py-2 transition-all ${theme === th.key ? "bg-white/15 ring-2 ring-white" : "hover:bg-white/10"}`}>
            <span className={`h-9 w-9 rounded-full bg-gradient-to-br ${th.swatch} shadow-lg`} />
            <span className="text-[10px] font-bold text-white/80">{t(th.nameKey)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function useRows(w, t) {
  return [
    { label: t("archetype"), value: w.archetype },
    { label: t("superpower"), value: w.superpower },
    { label: t("bestMatch"), value: w.best_match },
    { label: t("compat"), value: `${w.compatibility}%`, big: true },
    { label: t("hidden"), value: w.hidden_career },
    { label: t("growth"), value: w.growth_area },
  ];
}

function WrappedCard({ w, th }) {
  const { t } = useLang();
  const rows = useRows(w, t);
  return (
    <div id="wrapped-card" data-theme={th.key} className={`mx-auto aspect-[9/16] w-full max-w-xs overflow-hidden rounded-[28px] bg-gradient-to-br ${th.bg} p-6 text-white shadow-2xl`}>
      <div className="flex items-center justify-between">
        <span className="font-display text-lg font-extrabold">Nx<span className="text-amber-300">T</span></span>
        <span className="font-mono text-xs text-white/60">{w.year}</span>
      </div>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/80 [overflow-wrap:anywhere]" data-testid="wrapped-personal-title">{w.first_name ? `${w.first_name}'s Career Wrapped` : t("wrTitle")}</div>
      <div className="mt-4 space-y-3.5">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/50">{r.label}</div>
            <div className={`font-display font-extrabold leading-tight ${r.big ? `text-4xl ${th.accent}` : "text-lg"}`}>{r.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 border-t border-white/20 pt-3 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
        {t("discover")}
      </div>
    </div>
  );
}

/* Off-screen 1080x1920 Instagram Story render target */
function StoryCard({ w, th, refEl }) {
  const { t } = useLang();
  const rows = useRows(w, t);
  return (
    <div ref={refEl} className={`flex h-[1920px] w-[1080px] flex-col bg-gradient-to-br ${th.bg} p-[96px] text-white`}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="flex items-center justify-between">
        <span className="font-display text-[64px] font-extrabold">Nx<span className="text-amber-300">T</span></span>
        <span className="font-mono text-[36px] text-white/60">{w.year}</span>
      </div>
      <div className="mt-[8px] text-[30px] font-bold uppercase tracking-[0.2em] text-white/80 [overflow-wrap:anywhere]" data-testid="wrapped-image-personal-title">{w.first_name ? `${w.first_name}'s Career Wrapped` : t("wrTitle")}</div>
      <div className="mt-[120px] flex flex-1 flex-col justify-center space-y-[72px]">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="text-[28px] font-bold uppercase tracking-[0.25em] text-white/50">{r.label}</div>
            <div className={`font-display font-extrabold leading-[1.05] ${r.big ? `text-[168px] ${th.accent}` : "text-[68px]"}`}>{r.value}</div>
          </div>
        ))}
      </div>
      <div className="border-t-[3px] border-white/20 pt-[40px] text-center text-[30px] font-bold uppercase tracking-[0.3em] text-white/60">
        {t("discover")}
      </div>
    </div>
  );
}

export default function Wrapped() {
  const [w, setW] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("nxt_wrapped_theme") || "indigo");
  const th = THEMES.find((x) => x.key === theme) || THEMES[0];
  const pickTheme = (k) => { localStorage.setItem("nxt_wrapped_theme", k); setTheme(k); track("wrapped_theme_selected", { theme: k }); };
  const storyRef = useRef(null);
  const navigate = useNavigate();
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/wrapped").then(({ data }) => setW(data)).catch(() => navigate("/result"));
  }, [navigate]);

  const doShare = async (channel) => {
    const { data } = await api.post("/wrapped/share", { channel });
    const url = `${window.location.origin}${data.share_path}`;
    const text = `I just decoded my Career DNA on NxT — I'm ${w.archetype}! Discover yours 👇`;
    if (channel === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, "_blank");
    else if (channel === "linkedin") window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank");
    else if (channel === "copy") { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success(t("copied")); }
    else if (navigator.share) { navigator.share({ title: "My Career DNA", text, url }).catch(() => {}); }
    return url;
  };

  const renderStory = async () => {
    await document.fonts?.ready;
    return toPng(storyRef.current, { width: 1080, height: 1920, pixelRatio: 1, cacheBust: true, backgroundColor: "#0f172a" });
  };

  const downloadImage = async () => {
    setRendering(true);
    try {
      const dataUrl = await renderStory();
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `nxt-career-wrapped-${w.year}-${theme}.png`; a.click();
      api.post("/wrapped/share", { channel: "instagram_download" }).catch(() => {});
      track("wrapped_downloaded", { theme });
      toast.success(lang === "hi" ? "Save ho gaya! Story pe post karo 🎉" : "Saved! Post it to your Story 🎉");
    } catch (e) { toast.error("Could not render image. Please try again."); }
    finally { setRendering(false); }
  };

  const shareImage = async () => {
    setRendering(true);
    try {
      const dataUrl = await renderStory();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `nxt-career-wrapped-${w.year}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Career Wrapped", text: `I'm ${w.archetype} on NxT. Discover yours!` });
        api.post("/wrapped/share", { channel: "instagram_image" }).catch(() => {});
      } else {
        const a = document.createElement("a");
        a.href = dataUrl; a.download = file.name; a.click();
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share image."); }
    finally { setRendering(false); }
  };

  if (!w) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-indigo-950 pb-28">
      <div className="mx-auto max-w-md px-5 py-8 text-white">
        <div className="mb-6 flex items-center justify-between">
          <span className="w-24" />
          <span className="font-display text-xl font-extrabold text-white">Nx<span className="text-amber-300">T</span></span>
          <LangToggle dark className="w-auto" />
        </div>
        <motion.div initial={{ opacity: 0, y: 40, rotate: -3 }} animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} data-testid="wrapped-card">
          <WrappedCard w={w} th={th} />
        </motion.div>

        <ThemePicker theme={theme} setTheme={pickTheme} t={t} />

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button onClick={downloadImage} disabled={rendering} data-testid="download-story-btn"
            className="flex items-center justify-center gap-2 rounded-2xl bg-amber-400 py-3.5 text-sm font-bold text-slate-900 transition-all hover:bg-amber-300 active:scale-95 disabled:opacity-60">
            {rendering ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageDown className="h-5 w-5" />} {rendering ? t("preparing") : t("download")}
          </button>
          <button onClick={shareImage} disabled={rendering} data-testid="share-image-btn"
            className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-pink-500 py-3.5 text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-60">
            <Download className="h-5 w-5" /> {t("shareImage")}
          </button>
        </div>
        <p className="mt-2 text-center text-[11px] text-white/50">1080 × 1920 · Instagram Story ready</p>

        <div className="mt-7">
          <p className="text-center font-display text-xl font-bold">{t("wrChallenge")}</p>
          <p className="mt-1 text-center text-white/60">{t("wrChallenge2")}</p>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          {[
            { c: "whatsapp", label: "WhatsApp", cls: "bg-emerald-500 hover:bg-emerald-600" },
            { c: "linkedin", label: "LinkedIn", cls: "bg-blue-600 hover:bg-blue-700" },
            { c: "instagram", label: t("share"), cls: "bg-white/10 hover:bg-white/20 border border-white/20" },
          ].map((b) => (
            <button key={b.c} onClick={() => doShare(b.c)} data-testid={`share-${b.c}-btn`}
              className={`rounded-2xl ${b.cls} py-3.5 text-sm font-bold text-white transition-all active:scale-95`}>
              <Share2 className="mx-auto mb-1 h-5 w-5" />{b.label}
            </button>
          ))}
        </div>

        <button onClick={() => doShare("copy")} data-testid="share-copy-btn"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/5 py-3.5 font-bold text-white transition-colors hover:bg-white/10">
          {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />} {copied ? t("copied") : t("copyLink")}
        </button>

        <div className="mt-6"><CircleBoard dark /></div>

        <button onClick={() => navigate("/roadmap")} data-testid="wrapped-continue-btn"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-white py-4 font-bold text-slate-900 transition-all hover:bg-white/90 active:scale-[0.99]">
          {t("continueRoadmap")} <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      <div className="pointer-events-none fixed left-[-4000px] top-0" aria-hidden="true">
        <StoryCard w={w} th={th} refEl={storyRef} />
      </div>
    </div>
  );
}
